// Generic cascading <select> helper.
//
// A "cascade" is a chain of selects where each child's options depend on the
// parent's selected value. The parent's options group children via <optgroup
// label="...">, and the child selects present those options filtered by the
// parent's current value.
//
// Usage:
//
//   <div data-cascade data-cascade-auto-single>
//     <select data-cascade-level>...</select>   <!-- parent   -->
//     <select data-cascade-level>...</select>   <!-- child    -->
//     <select data-cascade-level>...</select>   <!-- grandchild -->
//   </div>
//
// Or, when the selects can't sit together under a single wrapper (e.g. a
// filter search with each level in its own grid column), mark the root with
// a comma-separated list of CSS selectors instead:
//
//   <div data-cascade data-cascade-selectors=".level_a, .level_b, .level_c">
//     ...columns containing the selects...
//   </div>
//
// Flags:
//   data-cascade-auto-single   If present, a child with exactly one non-blank
//                              option is auto-selected.
//
// The cascade cooperates with Tom Select: it destroys and reinitialises the
// Tom Select wrapper for each child when its option set changes, and calls
// the global `refreshTomSelect` helper (exposed in `tom-select.js`).

const CASCADE_ROOT_SELECTOR = '[data-cascade]'

// populateChild replaces a child's grouped markup with the flat option set for
// the parent's current value, so the <optgroup>s the templates were read from
// are gone from the live DOM. Keep what the server sent, to put back before
// Turbo snapshots the page for its cache. Some of these selects are rendered
// disabled server-side, so that goes with it.
const serverMarkup = new WeakMap()

function normalize(value) {
  if (value == null) return ''
  return String(value).trim()
}

function optionExists(options, value) {
  if (value == null || value === '') return false
  const normalizedValue = String(value)
  return options.some((option) => String(option.value) === normalizedValue)
}

function buildOptionNode({ value, text, disabled }) {
  const option = document.createElement('option')
  option.value = value
  option.textContent = text
  option.disabled = Boolean(disabled)
  return option
}

function buildTemplateMap(select) {
  const groups = {}

  select.querySelectorAll('optgroup').forEach((optgroup) => {
    const key = normalize(optgroup.getAttribute('label'))
    if (key === '') return

    groups[key] = Array.from(optgroup.querySelectorAll('option')).map((option) => ({
      value: option.value,
      text: option.textContent,
      disabled: option.disabled
    }))
  })

  return groups
}

function findBlankOption(select) {
  const blank = select.querySelector("option[value='']")
  if (!blank) return { value: '', text: '', disabled: false }

  return {
    value: blank.value,
    text: blank.textContent,
    disabled: blank.disabled
  }
}

function refreshTomSelect(select) {
  if (typeof global.refreshTomSelect === 'function') {
    global.refreshTomSelect(select)
  }
}

function destroyTomSelect(select) {
  if (select && select.tomselect) {
    select.tomselect.destroy()
  }
}

function resolveLevels(root) {
  const selectorList = root.getAttribute('data-cascade-selectors')

  if (selectorList) {
    const scope = root.closest('form') || root.closest('.filter-search') || document
    return selectorList
      .split(',')
      .map((selector) => scope.querySelector(selector.trim()))
      .filter((level) => level)
  }

  return Array.from(root.querySelectorAll('[data-cascade-level]'))
}

function initCascade(root) {
  if (root.dataset.cascadeInitialized === 'true') return

  const levels = resolveLevels(root)
  if (levels.length < 2) return

  const autoSelectSingle = root.hasAttribute('data-cascade-auto-single')
  const templates = []
  const blankOptions = []
  const initialValues = levels.map((level) => level.value || '')
  let syncing = false

  levels.forEach((level, index) => {
    if (index === 0) return
    if (!serverMarkup.has(level)) {
      serverMarkup.set(level, { options: level.innerHTML, disabled: level.disabled })
    }
    templates[index] = buildTemplateMap(level)
    blankOptions[index] = findBlankOption(level)
  })

  const populateChild = (parentIndex) => {
    const childIndex = parentIndex + 1
    const parent = levels[parentIndex]
    const child = levels[childIndex]
    if (!parent || !child) return

    const parentKey = normalize(parent.value)
    const optionsForGroup = parentKey === '' ? [] : (templates[childIndex]?.[parentKey] || [])
    const previousValue = child.value

    destroyTomSelect(child)
    child.innerHTML = ''

    const blankOption = blankOptions[childIndex]
    if (blankOption) child.append(buildOptionNode(blankOption))

    optionsForGroup.forEach((optionData) => {
      child.append(buildOptionNode(optionData))
    })

    const hasOptions = optionsForGroup.length > 0
    child.disabled = !hasOptions || parentKey === ''

    let nextValue = ''
    const initialValue = initialValues[childIndex]

    if (!child.disabled) {
      if (optionExists(optionsForGroup, initialValue)) {
        nextValue = initialValue
      } else if (optionExists(optionsForGroup, previousValue)) {
        nextValue = previousValue
      } else if (autoSelectSingle && optionsForGroup.length === 1) {
        nextValue = optionsForGroup[0].value
      }
    }

    child.value = nextValue
    initialValues[childIndex] = ''

    refreshTomSelect(child)
    child.dispatchEvent(new Event('change', { bubbles: true }))
  }

  const rebuildChainFrom = (parentIndex) => {
    for (let index = parentIndex; index < levels.length - 1; index += 1) {
      populateChild(index)
    }
  }

  const handleChange = (event) => {
    if (syncing) return
    const levelIndex = levels.indexOf(event.currentTarget)
    if (levelIndex === -1) return
    syncing = true
    rebuildChainFrom(levelIndex)
    syncing = false
  }

  levels.slice(0, -1).forEach((level) => {
    level.addEventListener('change', handleChange)
  })

  syncing = true
  rebuildChainFrom(0)
  syncing = false

  root.dataset.cascadeInitialized = 'true'
}

function initAllCascades() {
  document.querySelectorAll(CASCADE_ROOT_SELECTOR).forEach(initCascade)
}

// Put the server's grouped markup back and reset the init flag, so the snapshot
// Turbo caches is one the next turbo:load can reinitialise from. Restoring a
// page whose children had been flattened left buildTemplateMap with nothing to
// read, and every child came back empty and disabled even though its parent was
// still selected.
//
// Tom Select restores the markup it captured at init time when it is destroyed,
// which would undo this, and tom-select.js destroys every instance on this same
// event. Destroying the child's instance here first means that runs as a no-op,
// whichever order the two listeners were registered in.
document.addEventListener('turbo:before-cache', () => {
  document.querySelectorAll(CASCADE_ROOT_SELECTOR).forEach((root) => {
    resolveLevels(root).forEach((level) => {
      const original = serverMarkup.get(level)
      if (!original) return

      destroyTomSelect(level)
      level.innerHTML = original.options
      level.disabled = original.disabled
    })
    delete root.dataset.cascadeInitialized
  })
})

document.addEventListener('turbo:load', initAllCascades)
document.addEventListener('turbo:render', initAllCascades)
